import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { errMsg } from "./apiError";
import { Loader2, UserPlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POSTES = ["Gardien", "Défenseur central", "Latéral droit", "Latéral gauche", "Milieu défensif", "Milieu central", "Milieu offensif", "Ailier droit", "Ailier gauche", "Attaquant"];
const MAILLOTS = ["S", "M", "L", "XL", "XXL"];
const HEURES = ["09h00", "10h00", "11h00", "14h00", "15h00", "16h00"];

const empty = {
  prenom: "", nom: "", genre: "masculin", date_naissance: "", poste_principal: "", poste_secondaire: "",
  taille: "", poids: "", club_actuel: "", email: "", telephone: "",
  taille_maillot: "", detection_id: "kinshasa-2026", paiement_statut: "payée", montant_frais: "20",
  rdv_date: "", rdv_heure: "", note_coach: "",
};

const inputCls = "bg-[#0A0A0C] border border-[#27272A] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A059] w-full";

export default function AdminManualReg({ onCreated }) {
  const [form, setForm] = useState(empty);
  const [detections, setDetections] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/detections`).then((r) => setDetections(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        montant_frais: parseInt(form.montant_frais, 10),
        poste_secondaire: form.poste_secondaire || null,
        taille: form.taille || null,
        poids: form.poids || null,
        club_actuel: form.club_actuel || null,
        rdv_date: form.rdv_date || null,
        rdv_heure: form.rdv_heure || null,
        note_coach: form.note_coach || null,
      };
      const { data } = await axios.post(`${API}/admin/registrations/manuel`, payload, { withCredentials: true });
      toast.success(`${data.prenom} ${data.nom} enregistré`, {
        description: data.paiement_statut === "payée"
          ? `Référence ${data.reference}${data.equipe ? ` — assigné à ${data.equipe}` : ""}`
          : `Référence ${data.reference} — en attente de paiement`,
      });
      setForm(empty);
      onCreated?.();
    } catch (err) {
      toast.error(errMsg(err, "Erreur lors de l'enregistrement"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-[#C5A059]/40 bg-[#121214] p-6 lg:p-8 mb-10" data-testid="manual-reg-form">
      <div className="flex items-center gap-3 mb-2">
        <UserPlus size={18} className="text-[#C5A059]" />
        <p className="text-xs uppercase tracking-[0.25em] text-[#C5A059]">Inscription manuelle — joueur enregistré au bureau</p>
      </div>
      <p className="text-xs text-[#71717A] mb-6 leading-relaxed">
        Pour les joueurs inscrits directement au bureau sans passer par le site. La facture & contrat (avec les conditions) est émise et envoyée automatiquement au montant choisi. Ils apparaîtront dans la liste des inscrits, le CRM et les statistiques comme les autres.
      </p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <input required placeholder="Prénom *" value={form.prenom} onChange={set("prenom")} data-testid="manual-input-prenom" className={inputCls} />
        <input required placeholder="Nom *" value={form.nom} onChange={set("nom")} data-testid="manual-input-nom" className={inputCls} />
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Date de naissance *</label>
          <input required type="date" value={form.date_naissance} onChange={set("date_naissance")} data-testid="manual-input-date" className={`${inputCls} [color-scheme:dark]`} />
        </div>
        <select required value={form.genre} onChange={set("genre")} data-testid="manual-select-genre" className={inputCls}>
          <option value="masculin">Masculin</option>
          <option value="féminin">Féminin</option>
        </select>
        <select required value={form.poste_principal} onChange={set("poste_principal")} data-testid="manual-select-poste" className={inputCls}>
          <option value="" disabled>Poste principal *</option>
          {POSTES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={form.poste_secondaire} onChange={set("poste_secondaire")} data-testid="manual-select-poste2" className={inputCls}>
          <option value="">Poste secondaire</option>
          {POSTES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select required value={form.taille_maillot} onChange={set("taille_maillot")} data-testid="manual-select-maillot" className={inputCls}>
          <option value="" disabled>Taille du maillot *</option>
          {MAILLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input placeholder="Taille (cm)" value={form.taille} onChange={set("taille")} data-testid="manual-input-taille" className={inputCls} />
        <input placeholder="Poids (kg)" value={form.poids} onChange={set("poids")} data-testid="manual-input-poids" className={inputCls} />
        <input placeholder="Club actuel" value={form.club_actuel} onChange={set("club_actuel")} data-testid="manual-input-club" className={inputCls} />
        <input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} data-testid="manual-input-email" className={inputCls} />
        <input required type="tel" placeholder="Téléphone *" value={form.telephone} onChange={set("telephone")} data-testid="manual-input-telephone" className={inputCls} />
        <select required value={form.detection_id} onChange={set("detection_id")} data-testid="manual-select-detection" className={inputCls}>
          {detections.map((d) => <option key={d.id} value={d.id}>{d.nom}</option>)}
        </select>
        <select required value={form.paiement_statut} onChange={set("paiement_statut")} data-testid="manual-select-paiement" className={inputCls}>
          <option value="payée">Payée au bureau</option>
          <option value="en_attente_paiement">En attente de paiement</option>
        </select>
        <select required value={form.montant_frais} onChange={set("montant_frais")} data-testid="manual-select-montant" className={inputCls}>
          <option value="20">Frais : 20 $US</option>
          <option value="15">Frais : 15 $US</option>
          <option value="10">Frais : 10 $US</option>
          <option value="5">Frais : 5 $US</option>
          <option value="0">Gratuit (0 $US)</option>
        </select>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#71717A]">RDV bureau (optionnel)</label>
          <input type="date" value={form.rdv_date} onChange={set("rdv_date")} data-testid="manual-input-rdv-date" className={`${inputCls} [color-scheme:dark]`} />
        </div>
        <select value={form.rdv_heure} onChange={set("rdv_heure")} data-testid="manual-select-rdv-heure" className={inputCls}>
          <option value="">Heure du RDV</option>
          {HEURES.map((h) => <option key={h} value={h}>{h}</option>)}
        </select>
      </div>
      <textarea rows={2} placeholder="Note interne (optionnel)" value={form.note_coach} onChange={set("note_coach")} data-testid="manual-input-note"
        className={`${inputCls} mt-5 resize-none`} />

      <button type="submit" disabled={loading} data-testid="manual-submit-button"
        className="mt-6 inline-flex items-center gap-3 bg-[#C5A059] text-black px-8 py-3.5 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors disabled:opacity-60">
        {loading && <Loader2 size={15} className="animate-spin" />}
        Enregistrer le joueur
      </button>
    </form>
  );
}
